import React from 'react';
import ReactDOM from 'react-dom/client';
// Build: 2026-11-04T08:00:00Z
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import { App } from './app/App';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { useSessionStore } from './stores/sessionStore';
import { PrivacyPolicyPage, TermsOfServicePage, DataDeletionPage } from './pages/LegalPages';
import './styles/brand-identity.css';
import './styles/themes.css';
import './styles/global.css';
import './styles/scrollbars.css';
import './styles/management-ux.css';
import './styles/widget-builder.css';
import './styles/schedules-calendar.css';
import './styles/legacy-crm.css';
import './styles/ai-workspace.css';
import './styles/deals-kanban-ux.css';
import './styles/dashboard-ux.css';
import './i18n/config';

const handleAuthError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const isAuthError =
    message.includes('401') ||
    message.toLowerCase().includes('unauthorized') ||
    message.toLowerCase().includes('jwt expired') ||
    message.toLowerCase().includes('sessão expirada');

  if (isAuthError) {
    const hasSession = !!useSessionStore.getState().session;
    if (hasSession) {
      console.warn('[QueryClient] 401 detectado em consulta/mutação. Limpando sessão e redirecionando para login.');
      useSessionStore.getState().clear();
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/forgot-password') &&
        !window.location.pathname.startsWith('/reset-password') &&
        !window.location.pathname.includes('politica-de-privacidade') &&
        !window.location.pathname.includes('privacidade') &&
        !window.location.pathname.includes('termos-de-servico') &&
        !window.location.pathname.includes('termos') &&
        !window.location.pathname.includes('exclusao-de-dados') &&
        !window.location.pathname.includes('data-deletion') &&
        !window.location.pathname.includes('privacy') &&
        !window.location.pathname.includes('terms')
      ) {
        window.location.href = '/login';
      }
    }
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleAuthError,
  }),
  mutationCache: new MutationCache({
    onError: handleAuthError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const message = error instanceof Error ? error.message : String(error);
        if (
          message.includes('401') ||
          message.includes('403') ||
          message.includes('404') ||
          message.includes('429') ||
          message.toLowerCase().includes('unauthorized') ||
          message.toLowerCase().includes('forbidden') ||
          message.toLowerCase().includes('jwt expired') ||
          message.toLowerCase().includes('sessão expirada')
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

const path = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
const isPrivacy = path.includes('politica-de-privacidade') || path.includes('privacidade') || path.includes('privacy');
const isTerms = path.includes('termos-de-servico') || path === '/termos' || path === '/termos/' || path.includes('terms');
const isDataDeletion = path.includes('exclusao-de-dados') || path.includes('data-deletion');

if (isPrivacy) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <PrivacyPolicyPage />
    </React.StrictMode>
  );
} else if (isTerms) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <TermsOfServicePage />
    </React.StrictMode>
  );
} else if (isDataDeletion) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <DataDeletionPage />
    </React.StrictMode>
  );
} else {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter basename="/">
            <App />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
